import { Currency, currencyEquals, ETHER, WETH } from '@uniswap/sdk'
import { useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { tryParseAmount } from '../state/swap/hooks'
import { useTransactionAdder } from '../state/transactions/hooks'
import { useCurrencyBalance } from '../state/wallet/hooks'
import { useActiveWeb3React } from './index'
import { useWETHContract } from './useContract'
import { ChainId } from '@uniswap/sdk'

export enum WrapType {
  NOT_APPLICABLE,
  WRAP,
  UNWRAP
}

const NOT_APPLICABLE = {
  wrapType: WrapType.NOT_APPLICABLE,
  execute: undefined,
  inputError: undefined,
  isLoading: false
}

/**
 * Given the selected input and output currency, return a wrap callback
 * @param inputCurrency the selected input currency
 * @param outputCurrency the selected output currency
 * @param typedValue the user input value
 */
export default function useWrapCallback(
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined,
  typedValue: string | undefined
): {
  wrapType: WrapType
  execute?: undefined | (() => Promise<void>)
  inputError?: string
  isLoading: boolean
} {
  const { chainId, account } = useActiveWeb3React()
  const wethContract = useWETHContract()
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency)
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  // we can always parse the amount typed as the input currency, since wrapping is 1:1
  const inputAmount = useMemo(() => tryParseAmount(typedValue, inputCurrency), [inputCurrency, typedValue])
  const addTransaction = useTransactionAdder()

  const executeWrap = useCallback(async () => {
    if (!wethContract || !chainId || !inputCurrency || !outputCurrency || !inputAmount) return

    setIsLoading(true)
    try {
      if (inputCurrency === ETHER && currencyEquals(WETH[chainId], outputCurrency)) {
        // Wrap ETH to WETH
        const txReceipt = await wethContract.deposit({ value: `0x${inputAmount.raw.toString(16)}` })
        const nativeTokenSymbol = chainId === ChainId.BSC ? 'BNB' : 'K'
        const wrappedTokenSymbol = chainId === ChainId.BSC ? 'WBNB' : 'WK'
        addTransaction(txReceipt, {
          summary: t('wrapSummary', {
            amount: inputAmount.toSignificant(6),
            native: nativeTokenSymbol,
            wrapped: wrappedTokenSymbol
          })
        })
      } else if (currencyEquals(WETH[chainId], inputCurrency) && outputCurrency === ETHER) {
        // Unwrap WETH to ETH
        const txReceipt = await wethContract.withdraw(`0x${inputAmount.raw.toString(16)}`)
        const nativeTokenSymbol = chainId === ChainId.BSC ? 'BNB' : 'K'
        const wrappedTokenSymbol = chainId === ChainId.BSC ? 'WBNB' : 'WK'
        addTransaction(txReceipt, {
          summary: t('unwrapSummary', {
            amount: inputAmount.toSignificant(6),
            native: nativeTokenSymbol,
            wrapped: wrappedTokenSymbol
          })
        })
      }
    } catch (error) {
      console.error('Could not execute wrap/unwrap:', error)
    } finally {
      setIsLoading(false)
    }
  }, [wethContract, chainId, inputCurrency, outputCurrency, inputAmount, addTransaction, t])

  return useMemo(() => {
    if (!wethContract || !chainId || !inputCurrency || !outputCurrency) return NOT_APPLICABLE

    const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)

    if (inputCurrency === ETHER && currencyEquals(WETH[chainId], outputCurrency)) {
      return {
        wrapType: WrapType.WRAP,
        execute: sufficientBalance && inputAmount ? executeWrap : undefined,
        inputError: sufficientBalance ? undefined : t('insufficientBalance'),
        isLoading
      }
    } else if (currencyEquals(WETH[chainId], inputCurrency) && outputCurrency === ETHER) {
      return {
        wrapType: WrapType.UNWRAP,
        execute: sufficientBalance && inputAmount ? executeWrap : undefined,
        inputError: sufficientBalance ? undefined : t('insufficientBalance'),
        isLoading
      }
    } else {
      return NOT_APPLICABLE
    }
  }, [wethContract, chainId, inputCurrency, outputCurrency, inputAmount, balance, executeWrap, isLoading, t])
}
